<template>
	<ModalBox v-model="legacyModalVisible" size="lg" header-class="!p-2 -mb-8" wrapper-classes="lg:h-auto lg:max-h-none">
		<div class="grid grid-cols-12 gap-4 !py-2 !px-4">
			<div class="col-span-12 md:col-span-7 relative">
				<div class="relative aspect-video w-full rounded-lg overflow-hidden bg-black">
					<div
						class="absolute inset-0 z-0 flex flex-col items-center justify-center gap-2 text-surface-8 pointer-events-none">
						<span class="text-sm">Processing playback…</span>
					</div>
					<iframe
						:src="getIframeUrl()"
						class="absolute inset-0 w-full h-full z-[1]"
						frameborder="0"
						allow="autoplay; encrypted-media"
						scrolling="no"
						allowfullscreen
						webkitallowfullscreen
						mozallowfullscreen
						oallowfullscreen
						msallowfullscreen
					/>
				</div>
				<div class="mt-3" v-if="active && videoDuration > 0">
					<vue-slider
						v-model="rangeModel"
						:min="0"
						:max="videoDuration"
						:interval="1"
						:enable-cross="false"
						tooltip="none"
						:height="6"
						:dot-size="16"
						:rail-style="{ backgroundColor: '#4F5C87', borderRadius: '4px' }"
						:process-style="{ backgroundColor: '#0062FF' }"
						:dot-style="{ backgroundColor: '#ffffff', border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,.4)' }"
						@change="onRangeChange"
					/>
					<div class="flex justify-between text-tiny text-surface-7 mt-2">
						<span>00:00:00</span>
						<span>{{ humanTimeRange(videoDuration) }}</span>
					</div>
				</div>
			</div>

			<div class="col-span-12 md:col-span-5 flex flex-col justify-between self-center" v-if="active">
				<div>
					<h3 class="text-lg font-medium !mb-1 text-white">Clip video</h3>
					<p class="text-tiny text-surface-7 !mb-4">
						Drag the handles or enter exact start and end times to select a range.
					</p>

					<div class="mb-4">
						<label class="block text-tiny text-surface-7 mb-1.5">Clip's name</label>
						<FormInput v-model="name" @keydown.enter.prevent />
					</div>

					<div class="grid grid-cols-2 gap-3 mb-2">
						<div>
							<label class="block text-tiny text-surface-7 mb-1.5">Start time</label>
							<FormInput
								v-model="startInput"
								placeholder="00:00:00"
								@blur="applyStart"
								@keyup.enter="applyStart"
							/>
						</div>
						<div>
							<label class="block text-tiny text-surface-7 mb-1.5">End time</label>
							<FormInput
								v-model="endInput"
								placeholder="00:00:00"
								@blur="applyEnd"
								@keyup.enter="applyEnd"
							/>
						</div>
					</div>

					<div class="flex items-center justify-between text-tiny mb-4">
						<span class="text-surface-7">Clip length:
							<span class="text-white ml-1">{{ humanTimeRange(clipLength) }}</span>
						</span>
						<button
							type="button"
							v-if="!isFullRange"
							@click="resetRange"
							class="text-main-light hover:text-white transition-colors"
						>
							Reset
						</button>
					</div>
				</div>

				<div class="flex flex-col gap-2">
					<FormButton
						:disabled="!canClip"
						type="main"
						fullWidth
						class="justify-center"
						@click="onExportCustomRange"
					>
						{{ saveProcessing ? 'Saving…' : 'Clip' }}
					</FormButton>
					<FormButton type="secondary" fullWidth class="justify-center" @click="dismiss">
						Cancel
					</FormButton>
				</div>
			</div>
		</div>
	</ModalBox>
</template>

<script>
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import StreamService from '@/services/StreamService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import VueSlider from 'vue-3-slider-component'
import { track, EVENTS } from '@/lib/tracking'

export default {
	name: 'VodClipTrimmingModal',
	mixins: [legacyModalBridgeMixin],
	legacyModalId: 'modal-vod-clip-trimming',
	components: {
		ModalBox,
		FormButton,
		FormInput,
		VueSlider,
	},
	props: {
		hlsUrl: {
			type: String,
			required: true,
		},
		video: {
			type: Object,
			required: true,
		},
	},
	emits: ['request-vod-clip-range-created', 'modal-confirm'],
	data() {
		return {
			processing: false,
			active: false,
			rangeStart: 0,
			rangeEnd: 0,
			name: '',
			saveProcessing: false,
			startInput: '',
			endInput: '',
		}
	},
	computed: {
		videoDuration() {
			return Math.max(
				0,
				Math.floor(Number(this.video?.mediaInfo?.duration || this.video?.duration || 0))
			)
		},
		rangeModel: {
			get() {
				return [this.rangeStart, this.rangeEnd]
			},
			set(val) {
				if (!Array.isArray(val)) return
				this.rangeStart = val[0]
				this.rangeEnd = val[1]
			},
		},
		clipLength() {
			return Math.max(0, this.rangeEnd - this.rangeStart)
		},
		isFullRange() {
			return this.rangeStart === 0 && this.rangeEnd === this.videoDuration
		},
		canClip() {
			return (
				!this.saveProcessing &&
				this.name.trim() !== '' &&
				this.rangeStart >= 0 &&
				this.rangeEnd > this.rangeStart
			)
		},
	},
	methods: {
		onLegacyModalShow() {
			this.onInit()
		},
		onLegacyModalHide() {
			this.onDismiss()
		},
		onRangeChange(val) {
			if (Array.isArray(val)) {
				this.rangeStart = val[0]
				this.rangeEnd = val[1]
			}
			this.syncInputs()
		},
		parseTime(str) {
			if (typeof str !== 'string') return null
			const parts = str.trim().split(':').map((p) => Number(p))
			if (!parts.length || parts.some((n) => Number.isNaN(n))) return null
			let secs = 0
			if (parts.length === 3) secs = parts[0] * 3600 + parts[1] * 60 + parts[2]
			else if (parts.length === 2) secs = parts[0] * 60 + parts[1]
			else secs = parts[0]
			return Math.round(secs)
		},
		syncInputs() {
			this.startInput = this.humanTimeRange(this.rangeStart)
			this.endInput = this.humanTimeRange(this.rangeEnd)
		},
		applyStart() {
			const sec = this.parseTime(this.startInput)
			if (sec === null) return this.syncInputs()
			this.rangeStart = Math.max(0, Math.min(sec, this.rangeEnd - 1))
			this.syncInputs()
		},
		applyEnd() {
			const sec = this.parseTime(this.endInput)
			if (sec === null) return this.syncInputs()
			const max = this.videoDuration || sec
			this.rangeEnd = Math.min(max, Math.max(sec, this.rangeStart + 1))
			this.syncInputs()
		},
		resetRange() {
			this.rangeStart = 0
			this.rangeEnd = this.videoDuration
			this.syncInputs()
		},
		async onExportCustomRange() {
			if (!this.canClip) return
			this.saveProcessing = true
			try {
				const data = {
					name: this.name.trim(),
					startSec: this.rangeStart === 0 ? 1 : this.rangeStart,
					endSec: this.rangeEnd,
				}
				const resp = await StreamService.addVodClip(this.$route.params.streamId, this.video._id, data)
				track(EVENTS.CLIP_CREATED, {
					folderId: this.$route.params.streamId,
					fileId: this.video._id,
					clipLengthSec: data.endSec - data.startSec,
				})
				this.$emit('request-vod-clip-range-created', resp.data[0])
			} catch (err) {
				console.log(err)
				this.$notify({ group: 'error', text: 'Unable to add the clip.' })
			}
			this.saveProcessing = false
			this.dismiss()
		},
		onInit() {
			this.active = true
			this.rangeStart = 0
			this.rangeEnd = this.videoDuration
			this.name = ''
			this.syncInputs()
		},
		onDismiss() {
			this.active = false
			this.rangeStart = 0
			this.rangeEnd = 0
			this.name = ''
			this.startInput = ''
			this.endInput = ''
			this.saveProcessing = false
		},
		dismiss() {
			this.hideLegacyModal()
			setTimeout(() => {
				this.processing = false
			}, 1000)
		},
		getIframeUrl() {
			const appPath = process.env.NODE_ENV === 'production' ? '/app/' : '/'
			// slider=1 hides the in-player green range handles; this modal uses
			// its own vue-slider below the preview for trim selection.
			return `${appPath}static/player/index.html?url=${this.hlsUrl}&slider=1`
		},
		// Accumulate hours instead of moment HH:mm:ss (wraps at 24h).
		humanTimeRange(timeInSeconds) {
			const total = Math.max(0, Math.floor(Number(timeInSeconds) || 0))
			const h = Math.floor(total / 3600)
			const m = Math.floor((total % 3600) / 60)
			const s = total % 60
			const pad = (n) => String(n).padStart(2, '0')
			return `${pad(h)}:${pad(m)}:${pad(s)}`
		},
	},
}
</script>
